import type { Edge, Node } from "@xyflow/react";
import { connectNodes, patchEdge, type CanvasGraph, type GraphCommand } from "./canvas-commands";
import type { BoardNodeData } from "../types/board";

export type ConnectionPoint = "top" | "right" | "bottom" | "left";
const fourSides = ["top", "right", "bottom", "left"] as const;
export const CONNECTION_POINTS: Record<BoardNodeData["type"], readonly ConnectionPoint[]> = {
  rectangle: fourSides, circle: fourSides, diamond: fourSides, hexagon: fourSides,
  stickyNote: fourSides, orgChartCard: fourSides, text: ["top", "bottom"],
};
export function connectionPoints(type: string | undefined): readonly ConnectionPoint[] {
  return type && Object.prototype.hasOwnProperty.call(CONNECTION_POINTS, type) ? CONNECTION_POINTS[type as BoardNodeData["type"]] : [];
}
export type ConnectionEndpoints = Pick<Edge, "source" | "target" | "sourceHandle" | "targetHandle">;
export type ConnectionSide = "source" | "target";
export type ReconnectOrigin = { id: string; endpoints: ConnectionEndpoints; side: ConnectionSide };

export function connectionEndpoints(edge: ConnectionEndpoints): ConnectionEndpoints {
  return { source: edge.source, target: edge.target, sourceHandle: edge.sourceHandle, targetHandle: edge.targetHandle };
}
export function effectiveEndpoints(nodes: Node[], edge: ConnectionEndpoints): ConnectionEndpoints {
  const supported = (id: string) => connectionPoints(nodes.find(node => node.id === id)?.type).length > 0;
  return { ...connectionEndpoints(edge),
    sourceHandle: edge.sourceHandle || (supported(edge.source) ? "bottom" : edge.sourceHandle),
    targetHandle: edge.targetHandle || (supported(edge.target) ? "top" : edge.targetHandle) };
}
function sameEndpoints(a: ConnectionEndpoints, b: ConnectionEndpoints): boolean {
  return a.source === b.source && a.target === b.target && a.sourceHandle === b.sourceHandle && a.targetHandle === b.targetHandle;
}

// Render compatibility only: never pass these edges to history, clipboard or save.
export function displayedConnectionEdges(graph: CanvasGraph): Edge[] {
  return graph.edges.map(edge => {
    const effective = effectiveEndpoints(graph.nodes, edge);
    return sameEndpoints(edge, effective) ? edge : { ...edge, ...effective };
  });
}
export function isConnectionPoint(nodes: Node[], nodeId: string, handleId: string | null | undefined): boolean {
  const node = nodes.find(candidate => candidate.id === nodeId);
  return !!node && !node.hidden && node.connectable !== false &&
    connectionPoints(node.type).some(point => point === handleId);
}
export function canReconnect(edge: Edge, side: ConnectionSide): boolean {
  return !edge.hidden && edge.reconnectable !== false &&
    (typeof edge.reconnectable !== "string" || edge.reconnectable === side);
}

// A reconnect validates only the chosen endpoint; the fixed legacy endpoint may
// retain an unsupported type/handle, provided its node still exists.
export function isValidConnection(graph: CanvasGraph, proposed: ConnectionEndpoints, origin?: ReconnectOrigin): boolean {
  if (!graph.nodes.some(node => node.id === proposed.source) || !graph.nodes.some(node => node.id === proposed.target)) return false;
  const effective = effectiveEndpoints(graph.nodes, proposed);
  if (origin) {
    const current = graph.edges.find(edge => edge.id === origin.id);
    if (!current || !canReconnect(current, origin.side) || !sameEndpoints(current, origin.endpoints)) return false;
    const fixed = origin.side === "source" ? "target" : "source";
    const handle = fixed === "source" ? "sourceHandle" : "targetHandle";
    const previous = effectiveEndpoints(graph.nodes, current);
    if (effective[fixed] !== previous[fixed] || effective[handle] !== previous[handle]) return false;
    const movedHandle = origin.side === "source" ? proposed.sourceHandle : proposed.targetHandle;
    if (!isConnectionPoint(graph.nodes, proposed[origin.side], movedHandle)) return false;
  } else if (!isConnectionPoint(graph.nodes, proposed.source, proposed.sourceHandle) ||
    !isConnectionPoint(graph.nodes, proposed.target, proposed.targetHandle)) return false;
  if (effective.source === effective.target && effective.sourceHandle === effective.targetHandle) return false;
  return !graph.edges.some(edge => edge.id !== origin?.id && sameEndpoints(effectiveEndpoints(graph.nodes, edge), effective));
}

export function createConnection(edge: Edge): GraphCommand {
  return graph => isValidConnection(graph, edge) ? connectNodes(edge)(graph) : graph;
}
export function reconnectConnection(origin: ReconnectOrigin, proposed: ConnectionEndpoints): GraphCommand {
  return graph => {
    if (!isValidConnection(graph, proposed, origin)) return graph;
    const current = graph.edges.find(edge => edge.id === origin.id)!;
    const previous = effectiveEndpoints(graph.nodes, current), next = effectiveEndpoints(graph.nodes, proposed);
    if (sameEndpoints(previous, next)) return graph;
    // Patch just the moved endpoint. The current edge owns all other content,
    // including label/style edits made since the pointer gesture started.
    return patchEdge(current.id, origin.side === "source"
      ? { source: proposed.source, sourceHandle: proposed.sourceHandle }
      : { target: proposed.target, targetHandle: proposed.targetHandle })(graph);
  };
}
